import { FC, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { CustomCalendar } from 'src/App/AppRouter/Layout/TopBar/TopBarDatePicker/CustomCalendar'
import { ReactComponent as PlusIcon } from 'src/assets/svg/tinyPlusIcon.svg'
import { ReactComponent as WarningTriangleIcon } from 'src/assets/svg/warning-triangle-48.svg'
import { cn } from 'src/utils/cn'
import styles from './newCampaignComparisonModal.module.scss'

export interface ComparisonCampaign {
  id: string
  name: string
}

export interface CampaignComparisonPayload {
  campaignIds: string[]
  from: string
  // The shortcut the window was picked under, or null for a hand-picked range.
  // Stored with the comparison and shown beside its dates, never interpreted.
  quickCompare: string | null
  to: string
}

interface NewCampaignComparisonModalProps {
  campaigns: ComparisonCampaign[]
  isOpen: boolean
  onClose: () => void
  onSubmit: (payload: CampaignComparisonPayload) => void
}

const COPY = {
  campaignsHint:
    'Select 2 to 4 campaigns for an aggregated cross-source comparison.',
  campaignsStepSubtitle: 'Choosing specific campaigns.',
  campaignsSubtitle:
    'Depending on the type chosen, highlight specific campaigns.',
  campaignsTitle: 'Campaigns',
  close: 'Close',
  limitReached: (max: number) =>
    `Limit reached: You have selected ${max} campaigns. Unselect one to choose another.`,
  periodStepSubtitle: 'Which period of time do you want to compare data from?',
  periodStepTitle: 'Time period',
  periodSubtitle: 'Select a quick period or set custom dates.',
  periodTitle: 'Which period are you interested in?',
  pick: 'Min. 2',
  searchPlaceholder: 'Search campaigns by name',
  selecting: 'Selecting Campaigns',
  steps: '2 steps',
  submit: 'Generate Comparison',
  title: 'New Comparison',
}

const RANGE_FORMAT = 'MMM D, YYYY'
// Calendar days, not instants: the API reads these as UTC days, and an ISO
// timestamp from a picker east of UTC floors to the previous day.
const API_DATE_FORMAT = 'YYYY-MM-DD'

// Two gets the paired report — one campaign against another, a delta on every
// figure. Three or four gets the multi report, which is a table and a chart per
// widget instead. Four is where the design stops: a fifth column has no colour
// and no room to hold a number at 24px.
const MIN_CAMPAIGNS = 2
const MAX_CAMPAIGNS = 4

interface QuickPeriod {
  end: () => moment.Moment
  label: string
  start: () => moment.Moment
}

const QUICK_PERIODS: QuickPeriod[] = [
  {
    end: () => moment().subtract(1, 'day'),
    label: 'Yesterday',
    start: () => moment().subtract(1, 'day'),
  },
  {
    end: () => moment(),
    label: 'Last 7 days',
    start: () => moment().subtract(6, 'days'),
  },
  {
    end: () => moment(),
    label: 'Last 30 days',
    start: () => moment().subtract(29, 'days'),
  },
  {
    end: () => moment(),
    label: 'This Month',
    start: () => moment().startOf('month'),
  },
]

// The state the calendar is already in when the step opens, before a shortcut
// narrows it — so it is the default selection rather than a fifth period.
const CUSTOM_RANGE = 'Custom Range'

export const NewCampaignComparisonModal: FC<
  NewCampaignComparisonModalProps
> = ({ campaigns, isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState<1 | 2>(1)
  const [start, setStart] = useState<moment.Moment>(() =>
    moment().subtract(29, 'days')
  )
  const [end, setEnd] = useState<moment.Moment>(() => moment())
  const [activePeriod, setActivePeriod] = useState(CUSTOM_RANGE)
  // Bumped only when a quick period is chosen. CustomCalendar reads
  // initialStart/initialEnd once, at mount, so remounting is what makes it
  // adopt the new range and jump to its months. Deliberately NOT keyed on the
  // range itself: a remount between the two clicks of a manual range would
  // throw the first one away.
  const [calendarKey, setCalendarKey] = useState(0)
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase()

    return query
      ? campaigns.filter((campaign) =>
          campaign.name.toLowerCase().includes(query)
        )
      : campaigns
  }, [campaigns, search])

  if (!isOpen) {
    return null
  }

  const atLimit = selectedIds.length >= MAX_CAMPAIGNS

  const toggleCampaign = (id: string) => {
    setSelectedIds((current) => {
      if (current.includes(id)) {
        return current.filter((selected) => selected !== id)
      }

      // The limit is held rather than enforced by disabling every other card:
      // the message below says which one to give up.
      return current.length >= MAX_CAMPAIGNS ? current : [...current, id]
    })
  }

  const handleQuickPeriod = (period: QuickPeriod) => {
    setActivePeriod(period.label)
    setStart(period.start())
    setEnd(period.end())
    setCalendarKey((current) => current + 1)
  }

  const rangeLabel = `${start.format(RANGE_FORMAT)} – ${end.format(RANGE_FORMAT)}`

  const periodStep = (
    <>
      <div className={styles.paneHeader}>
        <div>
          <h3 className={styles.paneTitle}>{COPY.periodTitle}</h3>
          <p className={styles.paneSubtitle}>{COPY.periodSubtitle}</p>
        </div>

        <button
          aria-label={COPY.close}
          className={styles.closeButton}
          onClick={onClose}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className={styles.quickPeriods}>
        {[...QUICK_PERIODS.map((period) => period.label), CUSTOM_RANGE].map(
          (label) => (
            <button
              className={cn(
                styles.quickPeriod,
                activePeriod === label && styles.quickPeriodActive
              )}
              key={label}
              onClick={() => {
                const period = QUICK_PERIODS.find(
                  (option) => option.label === label
                )

                if (period) {
                  handleQuickPeriod(period)
                  return
                }

                setActivePeriod(CUSTOM_RANGE)
              }}
              type="button"
            >
              {label}
            </button>
          )
        )}
      </div>

      <div className={styles.calendarBox}>
        {/* The modal owns the footer — Next is this step's Apply — so the
            calendar reports each pick instead of waiting for its own button. */}
        <CustomCalendar
          className={styles.calendar}
          initialEnd={end}
          initialStart={start}
          key={calendarKey}
          monthsToShow={2}
          navLabel="Navigation for the selected period"
          onApply={() => undefined}
          onClose={() => undefined}
          onSelect={(nextStart, nextEnd) => {
            setActivePeriod(CUSTOM_RANGE)
            setStart(nextStart)
            setEnd(nextEnd ?? nextStart)
          }}
          showFooter={false}
          // Dots under the days that actually hold mentions, so a window is
          // picked knowing where the data is rather than by trial and error.
          source="social-listening"
        />
      </div>

      <div className={styles.footer}>
        <span className={styles.footerLabel}>{rangeLabel}</span>

        <button className={styles.backButton} onClick={onClose} type="button">
          Back
        </button>

        <button
          className={styles.primaryButton}
          onClick={() => setStep(2)}
          type="button"
        >
          Next
        </button>
      </div>
    </>
  )

  const campaignsStep = (
    <>
      <div className={styles.paneHeader}>
        <div>
          <h3 className={styles.paneTitle}>{COPY.campaignsTitle}</h3>
          <p className={styles.paneSubtitle}>{COPY.campaignsSubtitle}</p>
        </div>

        <button
          aria-label={COPY.close}
          className={styles.closeButton}
          onClick={onClose}
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className={styles.selectingRow}>
        <span className={styles.selectingLabel}>{COPY.selecting}</span>
        <span className={styles.minPill}>{COPY.pick}</span>
      </div>
      <p className={styles.paneSubtitle}>{COPY.campaignsHint}</p>

      <div className={styles.searchRow}>
        <input
          className={styles.search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={COPY.searchPlaceholder}
          value={search}
        />
        <span className={styles.selectedCount}>
          Selected {selectedIds.length} of {MAX_CAMPAIGNS}
        </span>
      </div>

      <div className={styles.campaignGrid}>
        {matches.map((campaign) => {
          const isSelected = selectedIds.includes(campaign.id)

          return (
            <button
              className={cn(
                styles.campaignCard,
                isSelected && styles.campaignCardSelected
              )}
              key={campaign.id}
              onClick={() => toggleCampaign(campaign.id)}
              type="button"
            >
              <span className={styles.campaignName} title={campaign.name}>
                {campaign.name}
              </span>

              {isSelected ? (
                <FontAwesomeIcon
                  className={styles.campaignIcon}
                  icon={faCheck}
                />
              ) : (
                <PlusIcon className={styles.campaignIcon} />
              )}
            </button>
          )
        })}
      </div>

      {atLimit && (
        <p className={styles.limitWarning}>
          <WarningTriangleIcon className={styles.limitIcon} />
          {COPY.limitReached(MAX_CAMPAIGNS)}
        </p>
      )}

      <div className={cn(styles.footer, styles.footerPlain)}>
        <button
          className={styles.backButton}
          onClick={() => setStep(1)}
          type="button"
        >
          Back
        </button>

        <button
          className={styles.primaryButton}
          disabled={selectedIds.length < MIN_CAMPAIGNS}
          onClick={() =>
            onSubmit({
              campaignIds: selectedIds,
              from: start.format(API_DATE_FORMAT),
              quickCompare: activePeriod === CUSTOM_RANGE ? null : activePeriod,
              to: end.format(API_DATE_FORMAT),
            })
          }
          type="button"
        >
          {COPY.submit}
        </button>
      </div>
    </>
  )

  // Portalled to the body: the app zooms .mainContainer to 0.9, so a fixed
  // overlay rendered inside it does not cover the viewport.
  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <aside className={styles.rail}>
          {/* One block: the rail's 64px gap belongs between this and the steps,
              not between the title and its own caption, which is 4px. */}
          <div>
            <h2 className={styles.railTitle}>{COPY.title}</h2>
            <p className={styles.railSteps}>{COPY.steps}</p>
          </div>

          <ol className={styles.stepList}>
            {[
              {
                subtitle: COPY.periodStepSubtitle,
                tight: false,
                title: COPY.periodStepTitle,
              },
              {
                subtitle: COPY.campaignsStepSubtitle,
                tight: true,
                title: COPY.campaignsTitle,
              },
            ].map((entry, index) => (
              <li
                className={cn(
                  styles.step,
                  step === index + 1 && styles.stepActive
                )}
                key={entry.title}
              >
                <span className={styles.stepNumber}>{index + 1}</span>

                <span className={styles.stepText}>
                  <span className={styles.stepTitle}>{entry.title}</span>
                  {step === index + 1 && (
                    <span
                      className={cn(
                        styles.stepSubtitle,
                        entry.tight && styles.stepSubtitleTight
                      )}
                    >
                      {entry.subtitle}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </aside>

        <section className={styles.pane}>
          {step === 1 ? periodStep : campaignsStep}
        </section>
      </div>
    </div>,
    document.body
  )
}
